import type { z } from 'zod';
import { montarCaminho, requisitar } from '../api/client.js';
import { ehErroApi, mensagemDoErro, MENSAGEM_GENERICA } from '../api/erros.js';
import type { components } from '../api/schema.js';
import type { Sessao } from './tipos.js';
import { sessaoSchema } from './tipos.js';

/**
 * Cliente das rotas de autenticação do painel.
 *
 * Passa pelo cliente tipado de `src/api/client.ts`: os caminhos são conferidos
 * contra `schema.ts`, GERADO do `../api/openapi.json`. A validação por zod
 * continua — o OpenAPI descreve o contrato, não garante o corpo que chega.
 *
 *   POST /autenticacao/entrar  { usuario, senha } → 200 { usuario, tenant } + cookie
 *   POST /autenticacao/sair                       → 200 {} (idempotente)
 *   GET  /autenticacao/eu                         → 200 { usuario, tenant } | 401
 */

const CAMINHO_ENTRAR = montarCaminho('/autenticacao/entrar');
const CAMINHO_EU = montarCaminho('/autenticacao/eu');
const CAMINHO_SAIR = montarCaminho('/autenticacao/sair');

/**
 * O zod é conferido, em tempo de compilação, contra o tipo gerado do OpenAPI:
 * se a API renomear um campo da identidade, isto para de compilar aqui em vez
 * de virar tela vazia em produção.
 */
const identidadeSchema: z.ZodType<components['schemas']['IdentidadeResponse']> = sessaoSchema;

/** Falha de autenticação já traduzida para o supervisor. */
export class ErroAutenticacao extends Error {
  override readonly name = 'ErroAutenticacao';

  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    super(mensagem);
  }
}

/**
 * Traduz a falha HTTP; deixa passar o que não é resposta.
 *
 * Falha de rede (`TypeError` do `fetch`) continua subindo crua de propósito:
 * quem chama distingue "o middleware recusou" de "não houve middleware
 * nenhum" — a tela de login mostra texto próprio no segundo caso.
 */
function traduzir(erro: unknown): never {
  if (ehErroApi(erro)) {
    throw new ErroAutenticacao(erro.status, mensagemDoErro(erro));
  }

  throw erro;
}

function sessaoDoCorpo(corpo: unknown, status: number): Sessao {
  const analisado = identidadeSchema.safeParse(corpo);

  if (!analisado.success) {
    throw new ErroAutenticacao(status, MENSAGEM_GENERICA);
  }

  return analisado.data;
}

/**
 * Autentica no painel. O middleware devolve o cookie de sessão; nada é
 * guardado pelo JavaScript.
 *
 * @throws {ErroAutenticacao} credencial inválida, limite de taxa ou falha do middleware.
 */
export async function entrar(credenciais: {
  readonly usuario: string;
  readonly senha: string;
}): Promise<Sessao> {
  try {
    const corpo = await requisitar(CAMINHO_ENTRAR, {
      metodo: 'POST',
      corpo: { usuario: credenciais.usuario, senha: credenciais.senha },
    });

    return sessaoDoCorpo(corpo, 200);
  } catch (erro) {
    if (erro instanceof ErroAutenticacao) {
      throw erro;
    }

    return traduzir(erro);
  }
}

/**
 * Descobre a sessão corrente pelo cookie.
 *
 * Devolve `null` em 401 — não autenticado é resposta esperada no boot do app,
 * não erro. Qualquer outro status vira `ErroAutenticacao`, porque aí o painel
 * não sabe se há sessão e não pode assumir que não há.
 */
export async function consultarSessao(): Promise<Sessao | null> {
  try {
    return sessaoDoCorpo(await requisitar(CAMINHO_EU), 200);
  } catch (erro) {
    if (ehErroApi(erro) && erro.status === 401) {
      return null;
    }

    if (erro instanceof ErroAutenticacao) {
      throw erro;
    }

    return traduzir(erro);
  }
}

/**
 * Encerra a sessão no middleware. Idempotente: sem sessão válida também
 * responde 200, então o painel nunca fica preso numa sessão que já morreu.
 */
export async function sair(): Promise<void> {
  await requisitar(CAMINHO_SAIR, { metodo: 'POST' });
}
